import { DialogProps, useTheme } from '@lsy-netline/netline-ui';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, styled } from 'netline-ui';
import services from 'services';
import { DialogErrorItem, ErrorAction } from 'types';

import ErrorActions from './ErrorActions';
import ErrorDetails from './ErrorDetails';
import styles, { errorDialogClasses } from './ErrorDialog.styles';
import ErrorText from './ErrorText';

const ErrorDialogRoot = styled(Dialog, { name: errorDialogClasses.$name })(styles);

type ErrorDialogProps = Omit<DialogProps, 'open'> & { error?: DialogErrorItem | null };

const ErrorDialog = ({ className, classes: classesProp, error: errorProp, ...props }: ErrorDialogProps) => {
  const duration = useTheme().transitions.duration.standard;
  const [errorAtClose, setErrorAtClose] = useState<DialogErrorItem | null | undefined>(errorProp);
  useEffect(() => {
    if (!errorProp) {
      const timerId = setTimeout(() => {
        setErrorAtClose(null);
      }, duration);

      return () => {
        clearTimeout(timerId);
      };
    }

    setErrorAtClose(errorProp);
    return undefined;
  }, [duration, errorProp]);

  const actionsProp = errorProp?.actions;
  const actions = useMemo<ErrorAction[]>(
    () =>
      actionsProp ?? [
        {
          id: 'ok',
          text: 'buttons.ok',
          primary: true,
          onClick: () => {
            services.appFrame.error.dialog.closeError();
          },
        },
      ],
    [actionsProp],
  );

  const error = errorProp || errorAtClose;
  if (!error) {
    return null;
  }

  const { title, message, details } = error;

  return (
    <ErrorDialogRoot
      className={clsx(className, errorDialogClasses.root)}
      data-testid="ErrorDialog-root"
      classes={{
        paper: errorDialogClasses.paper,
      }}
      open={!!errorProp}
      {...props}
    >
      <DialogTitle type="error" className={errorDialogClasses.title} data-testid="ErrorDialog-title">
        <ErrorText text={title} />
      </DialogTitle>
      <DialogContent className={errorDialogClasses.message} data-testid="ErrorDialog-message">
        <ErrorText text={message} />
      </DialogContent>
      {details && (
        <DialogContent className={errorDialogClasses.details}>
          <ErrorDetails data-testid="ErrorDialog-details" details={details} />
        </DialogContent>
      )}
      <DialogActions className={errorDialogClasses.actions}>
        <ErrorActions actions={actions} justifyContent="flex-end" />
      </DialogActions>
    </ErrorDialogRoot>
  );
};

export default ErrorDialog;
